import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { AlertMessage } from './models/alert-message';
import { AlertSeverityEnum } from '../../constants/alert-severity-enum';
import { AlertMessageOptions } from './models/alert-message-options';

@Injectable({
    providedIn: 'root',
})
export class AlertService {
    private alertsSubject: BehaviorSubject<AlertMessage[]> =
        new BehaviorSubject<AlertMessage[]>([]);
    public alerts$: Observable<AlertMessage[]> =
        this.alertsSubject.asObservable();

    constructor() {}

    /**
     * Displaying a successful alert to the user with a message.
     * A GUID will be generated for the alert.
     * @param message The message to display
     * @param sticky Whether the alert should be sticky
     * @param options Additional options for the alert
     */
    displaySuccessAlert(
        message: string,
        sticky: boolean = false,
        options: AlertMessageOptions = {},
    ): void {
        this.addAlert({
            id: this.generateId(),
            severity: AlertSeverityEnum.SUCCESS,
            message: message,
            sticky: sticky,
            options: options,
        });
    }

    /**
     * Displaying a warning alert to the user with a message.
     * A GUID will be generated for the alert.
     * @param message The message to display
     * @param sticky Whether the alert should be sticky
     * @param options Additional options for the alert
     */
    displayWarningAlert(
        message: string,
        sticky: boolean = false,
        options: AlertMessageOptions = {},
    ): void {
        this.addAlert({
            id: this.generateId(),
            severity: AlertSeverityEnum.WARNING,
            sticky: sticky,
            message: message,
            options: options,
        });
    }

    /**
     * Displaying an error alert to the user with a message.
     * A GUID can be provided for the alert, otherwise one will be generated.
     * @param message The message to display
     * @param sticky Whether the alert should be sticky
     * @param options Additional options for the alert
     */
    displayErrorAlert(
        message: string,
        sticky: boolean = true,
        options: AlertMessageOptions = {},
    ): void {
        this.addAlert({
            id: this.generateId(),
            severity: AlertSeverityEnum.ERROR,
            message: message,
            sticky: sticky,
            options: options,
        });
    }

    /**
     * Removing an alert from the list of alerts.
     * @param id The ID that is used to remove the alert from the list
     */
    removeAlert(id: string): void {
        this.alertsSubject.next(
            this.alertsSubject.value.filter((alert) => alert.id !== id),
        );
    }

    /**
     * Get the current list of alerts synchronously.
     * @returns The current array of AlertMessage objects
     */
    getCurrentAlerts(): AlertMessage[] {
        return this.alertsSubject.getValue();
    }

    private addAlert(alertMessage: AlertMessage): void {
        this.alertsSubject.next([...this.alertsSubject.value, alertMessage]);

        if (!alertMessage.sticky) {
            setTimeout(
                () => this.removeAlert(alertMessage.id),
                alertMessage.options.durationInMs ?? 10000,
            );
        }
    }

    private generateId(): string {
        return uuidv4();
    }
}
